/*
 * Copyright (c) 2020
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, OnInit, ViewContainerRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { HttpService } from '../../services/http-service.service';
import { ScrollToConfigOptions, ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import * as moment from 'moment';
import Quill from 'quill';
import BlotFormatter from 'quill-blot-formatter';
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop';
import { ConfirmationDialogComponent } from 'src/app/components/fields';
import { Subject } from 'rxjs';
import { IbisPreparedStatement, IbisReport } from '../../model';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
Quill.register('modules/blotFormatter', BlotFormatter);
import { LogService } from 'src/app/services';
@Component({
  selector: 'app-ibis-report',
  templateUrl: './ibis-report.component.html',
  styleUrls: ['./ibis-report.component.scss']
})

export class IbisReportComponent implements OnInit {
  @ViewChild('reportForm', {static: true}) reportForm:ViewContainerRef;
  ibisReport = IbisReport.createNewIbisWithData();
  loading: boolean = false;
  today: Date = new Date();
  quillModules = {
    blotFormatter: {},
    toolbar: [
      [{ 'align': null}, {'align': 'center'}, {'align': 'right'}, {'align': 'justify'}],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image'],
      ['clean'],
    ]
  };
  navBarItems: NavBarItem[] = [
    {NAME: 'Data', SELECTED: false, TARGET: 'BASIC_INFO'},
    {NAME: 'Future Prospects for the Business', SELECTED: false, TARGET: 'FUTURE_PROSPECTS'},
    {NAME: 'Industry Definition', SELECTED: false, TARGET: 'INDUSTRY_DEFINITION'},
    {NAME: 'Executive Summary', SELECTED: false, TARGET: 'EXECUTIVE_SUMMARY'},
    {NAME: 'Current Performance', SELECTED: false, TARGET: 'CURRENT_PERFORMANCE'},
    {NAME: 'Industry Outlook', SELECTED: false, TARGET: 'INDUSTRY_OUTLOOK'},
    {NAME: 'Products and Services', SELECTED: false, TARGET: 'PRODUCT_SERVICES'},
    {NAME: 'Industry Cost', SELECTED: false, TARGET: 'INDUSTRY_COST'},
    {NAME: 'Key External Drivers / IBIS Blurbs', SELECTED: false, TARGET: 'EXTERNAL_DRIVERS'},
  ];
  files: NgxFileDropEntry[] = [];
  fileArray: any[] = [];
  confirmationModal: BsModalRef;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public httpService: HttpService,
    private scrollToService: ScrollToService,
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private logService : LogService
  ) { }

  ngOnInit(): void {
    if(!this.ibisReport.DATA.EXTERNAL_DRIVERS || this.ibisReport.DATA.EXTERNAL_DRIVERS.length === 0){
      this.ibisReport.DATA.EXTERNAL_DRIVERS = [];
    }

    this.route.params.subscribe((params: Params) => {
      if(params.id){
        this.ibisReport.ID = params.id;
        this.getIbisReport();
      }
      if(params.data){
        let info = JSON.parse(window.atob(params.data));
        this.ibisReport.NAME = info.NAME;
        this.ibisReport.CODE = info.CODE;
        if(info.DATE)
          this.ibisReport.DATE = info.DATE;
        if(info.PRIOR)
          this.ibisReport.DATA.GROWTH_RATE_PRIOR = info.PRIOR;
        if(info.NEXT)
          this.ibisReport.DATA.GROWTH_RATE_NEXT = info.NEXT;
      }
      if(this.ibisReport.CODE)
        this.getLatestIbisReport();
    });
  }

  /** Go back to the ibis report list page */
  goBack(): void{
    this.router.navigate(['/manage/research/ibis']);
  }

  /** Save ibis report */
  saveReport(): void{
    const contentParam: FormData = new FormData();
    contentParam.append('content', JSON.stringify(this.ibisReport));
    this.fileArray.map(data => {
      contentParam.append('files', data);
    });

    this.loading = true;
    if(this.ibisReport.ID){
      this.httpService.updateIbisDataReport(this.ibisReport.ID, contentParam)
        .subscribe((resp) => {
          this.loading = false;
          if(resp.success){
            this.router.navigate(['/manage/research/ibis/edit/' + resp.result.ID]);
          }
        }, (error) => {
          this.loading = false;
          this.logService.log(error);
        });
    }else{
      this.httpService.createIbisDataReport(contentParam)
        .subscribe((resp) => {
          this.loading = false;
          if(resp.success){
            this.router.navigate(['/manage/research/ibis/edit/' + resp.result.ID]);
          }
        }, (error) => {
          this.loading = false;
          this.logService.log(error);
        });
    }
  }

  /** Add blurb item into list */
  addAnother(): void{
    if(this.ibisReport.ID)
      this.createIbisPs();
    else
      this.ibisReport.DATA.EXTERNAL_DRIVERS.push(new IbisPreparedStatement());
  }

  /** Scroll to the particular item when item clicked */
  onSelect(item: NavBarItem): void {
    this.navBarItems.map(i => i.SELECTED = false);
    item.SELECTED = true;
    this.scrollTo(item.TARGET);
  }

  /** Scroll to target element */
  scrollTo(target: string): void {
    const config: ScrollToConfigOptions = { target };
    this.scrollToService.scrollTo(config);
  }

  /** Open calender in month view mode */
  onOpenCalendar(container): void {
    container.monthSelectHandler = (event: any): void => {
      container._store.dispatch(container._actions.select(event.date));
    };
    container.setViewMode('month');
  }

  /** Format date when date selected */
  formatDate(): void{
    this.ibisReport.DATE = moment(this.ibisReport.DATE).format('MM-YYYY');
  }

  // trackByFn() - For tracking an array item.
  trackByFn(index: any): void {
    return index;
  }

  /** Remove Ibis PS */
  removeIbisPs(index: number): void{
    if(this.ibisReport.ID){
      this.httpService.removeIbisReportBlurb(this.ibisReport.ID, this.ibisReport.DATA.EXTERNAL_DRIVERS[index].ID)
        .subscribe((resp) => {
          if(resp.success){
            this.ibisReport.DATA.EXTERNAL_DRIVERS.splice(index, 1);
          }else this.logService.log(resp);
        }, (error) => {
          this.logService.log(error);
        });
    }else{
      this.ibisReport.DATA.EXTERNAL_DRIVERS.splice(index, 1);
    }
  }

  /** Create Ibis PS */
  createIbisPs(): void{
    this.httpService.createIbisReportBlurb(this.ibisReport.ID)
      .subscribe((resp) => {
        if(resp.success){
          let blurb = new IbisPreparedStatement();
          blurb.ID = resp.result.ID;
          this.ibisReport.DATA.EXTERNAL_DRIVERS.push(blurb);
        }else this.logService.log(resp);
      }, (error) => {
        this.logService.log(error);
      });
  }

  /** Get Ibis Report */
  getIbisReport(): void{
    this.loading = true;
    this.httpService.getIbisReportById(this.ibisReport.ID)
      .subscribe((resp) => {
        this.loading = false;
        if(resp.success){
          this.ibisReport = new IbisReport(resp.result);
        }else this.logService.log(resp);
      }, (error) => {
        this.loading = false;
        this.logService.log(error);
      });
  }

  /** Get latest Ibis Report */
  getLatestIbisReport(isClick: boolean = false): void{
    this.httpService.getLatestIbisReport(this.ibisReport.CODE)
      .subscribe((resp) => {
        if(resp.success){
          if(resp.result){
            this.ibisReport.DATA.FUTURE_PROSPECTS = resp.result.DATA.FUTURE_PROSPECTS;
          }else if(isClick){
            this.confirmationModal = this.modalService.show(ConfirmationDialogComponent, { class: 'modal-sm modal-dialog-centered' });
            const ackSubject = new Subject<any>();
            this.confirmationModal.content.subject = ackSubject;
            this.confirmationModal.content.title = 'Section 2.11';
            this.confirmationModal.content.text = 'No Data available';
            this.confirmationModal.content.actionButton = 'Ok';
            this.confirmationModal.content.showCancel = false;
            this.confirmationModal.content.actionButtonClass = 'default';

            ackSubject.subscribe(() => {});
          }
        }else this.logService.log(resp);
      }, (error) => {
        this.logService.log(error);
      });
  }

  // dropped() - Do operations when file is dropped
  dropped(files: NgxFileDropEntry[]): void {
    this.files = files;
    this.fileArray= [];

    for (const droppedFile of files) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          // Add file into array for Uploading file to the server
          if(file.type === 'application/pdf'){
            if(this.fileArray.length == 0){
              this.fileArray.push(file);
            }
          }
        });
      }
    }
  }

  // Pick file from system
  openFileSelector(): void{}

  // Toggle draft
  toggleDraft(): void{
    this.ibisReport.DRAFT = !this.ibisReport.DRAFT;
  }
}

export class NavBarItem{
  NAME: string;
  SELECTED: boolean;
  TARGET: string;
}
